// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Institution } from '../types';
import { num, delta, esc, tip } from '../format';
import { gloss } from '../glossary';
import { squarify } from '../utils/squarify';
import { openInstitution } from '../main';

type RankBy = 'now' | 'lost' | 'pct';

// Stable colour per sector for the treemap.
const SECTOR_COLOUR: Record<string, string> = {
  MAJ: '#1e3a5f', AUS: '#2f7d8f', 'FOR BANK': '#6a5aa0', 'FOR BR': '#8a6db0',
  CU: '#b06a1f', BS: '#c79a33', ADO: '#7a8699', 'Bank@Post': '#0f766e',
};
const sectorColour = (s: string): string => SECTOR_COLOUR[s] ?? '#7a8699';

export function renderBanks(root: HTMLElement, data: Dataset): void {
  const { institutions, national } = data;
  const years = national.years;
  const N = years.length;
  const withBranches = institutions.filter((i) => i.branchPeak > 0);
  let rankBy: RankBy = 'lost';

  root.innerHTML = `
    <div class="view-head">
      <h2>Who is closing the branches</h2>
      <p>Every bank, credit union and building society by its branch network. The big four have driven most of the retreat — but rank by share and some smaller lenders have shrunk faster. Click any bank for its full record.</p>
    </div>
    <div class="controls">
      <span class="control-label">Rank by</span>
      <div class="seg" aria-label="Rank by">
        <button data-rank="lost" aria-pressed="true">Branches closed</button>
        <button data-rank="now" aria-pressed="false">Branches now</button>
        <button data-rank="pct" aria-pressed="false">% closed</button>
      </div>
    </div>
    <div class="panel">
      <div class="panel-head"><h3>Branch networks, ${years[0]} → ${years[N - 1]}</h3><p>Bar length is branches now; the pale extension is what has been closed since ${years[0]}.</p></div>
      <div class="barlist" id="bars"></div>
    </div>
    <div class="two-col">
      <div class="panel">
        <div class="panel-head"><h3>The retreat, year by year</h3><p>Branch count each June for the largest networks. Each bank is shaded against its own ${years[0]} peak, so the fade left-to-right is the closures.</p></div>
        <div class="chart-wrap scroll-x" id="matrix"></div>
      </div>
      <div class="panel">
        <div class="panel-head"><h3>Branches today, by institution</h3><p>Area is branches in ${years[N - 1]}, coloured by ${gloss('adi', 'institution type')}. Click a block to open the bank.</p></div>
        <div class="chart-wrap" id="tree"></div>
        <div class="legend" id="tree-legend"></div>
      </div>
    </div>
  `;

  const barsEl = root.querySelector('#bars') as HTMLElement;

  function drawBars(): void {
    let list = withBranches.filter((i) => (rankBy === 'pct' ? i.branch2017 >= 30 : true));
    list = [...list].sort((a, b) =>
      rankBy === 'now' ? b.branchNow - a.branchNow
      : rankBy === 'pct' ? (a.branchChange ?? 0) - (b.branchChange ?? 0)
      : (a.branchNow - a.branch2017) - (b.branchNow - b.branch2017));
    const top = list.slice(0, 18);
    const maxPeak = Math.max(...top.map((i) => Math.max(i.branch2017, i.branchNow)), 1);
    barsEl.innerHTML = top.map((i) => {
      const nowPct = (i.branchNow / maxPeak) * 100;
      const lostPct = (Math.max(0, i.branch2017 - i.branchNow) / maxPeak) * 100;
      const valTxt = rankBy === 'pct' ? delta(i.branchChange) : rankBy === 'now' ? num(i.branchNow) : num(i.branchNow - i.branch2017);
      return `<div class="barrow" data-abn="${esc(i.abn)}" data-tip="${tip(`${i.name}\n${num(i.branch2017)} → ${num(i.branchNow)} branches (${delta(i.branchChange)})`)}">
        <span class="lbl">${esc(shortName(i.name))}</span>
        <span class="track"><span style="width:${nowPct}%;background:var(--branch)"></span><span style="position:absolute;left:${nowPct}%;top:0;width:${lostPct}%;height:100%;background:var(--branch);opacity:.22"></span></span>
        <span class="val ${rankBy !== 'now' ? 'down' : ''}">${valTxt}</span>
      </div>`;
    }).join('');
    barsEl.querySelectorAll<HTMLElement>('[data-abn]').forEach((r) =>
      r.addEventListener('click', () => openInstitution(r.dataset.abn!)),
    );
  }

  // matrix
  const matrixEl = root.querySelector('#matrix') as HTMLElement;
  const topForMatrix = [...withBranches].sort((a, b) => b.branch2017 - a.branch2017).slice(0, 14);
  {
    const rowH = 26, labelW = 150, cellW = Math.max(30, Math.min(56, 640 / N));
    const w = labelW + cellW * N + 8;
    const h = topForMatrix.length * rowH + 26;
    let cells = '';
    topForMatrix.forEach((i, ri) => {
      const peak = Math.max(...i.series.branch, 1);
      const y = 22 + ri * rowH;
      cells += `<text x="${labelW - 6}" y="${y + rowH / 2 + 4}" text-anchor="end" class="axis-text" style="cursor:pointer" data-abn="${esc(i.abn)}">${esc(shortName(i.name, 22))}</text>`;
      i.series.branch.forEach((v, ci) => {
        const x = labelW + ci * cellW;
        const t = v / peak;
        const col = mix('#eef2f5', '#1e3a5f', t);
        cells += `<rect class="matrix-cell click" x="${x}" y="${y}" width="${cellW - 2}" height="${rowH - 3}" rx="2" fill="${col}" data-abn="${esc(i.abn)}" data-tip="${tip(`${i.name}\n${years[ci]}: ${num(v)} branches`)}" /><text x="${x + (cellW - 2) / 2}" y="${y + rowH / 2 + 3}" text-anchor="middle" style="font-size:9px;fill:${t > 0.55 ? '#fff' : 'var(--text-secondary)'}">${v}</text>`;
      });
    });
    let xlab = '';
    years.forEach((yr, ci) => { xlab += `<text x="${labelW + ci * cellW + (cellW - 2) / 2}" y="16" text-anchor="middle" class="axis-text">${String(yr).slice(2)}</text>`; });
    matrixEl.innerHTML = `<svg class="chart" viewBox="0 0 ${w} ${h}" width="${w}" role="img" aria-label="Branch count by bank and year">${xlab}${cells}</svg>`;
    matrixEl.querySelectorAll<SVGElement>('[data-abn]').forEach((c) => c.addEventListener('click', () => openInstitution((c as any).dataset.abn)));
  }

  // treemap
  const treeEl = root.querySelector('#tree') as HTMLElement;
  {
    const list = [...withBranches].filter((i) => i.branchNow > 0).sort((a, b) => b.branchNow - a.branchNow);
    const tw = 460, th = 300;
    const rects = squarify(list.map((i) => i.branchNow), tw, th);
    let cells = '';
    rects.forEach((r, idx) => {
      const i = list[idx];
      const showLabel = r.w > 54 && r.h > 22;
      cells += `<g class="node click" data-abn="${esc(i.abn)}" data-tip="${tip(`${i.name}\n${num(i.branchNow)} branches`)}">
        <rect x="${r.x.toFixed(1)}" y="${r.y.toFixed(1)}" width="${Math.max(0, r.w - 1).toFixed(1)}" height="${Math.max(0, r.h - 1).toFixed(1)}" fill="${sectorColour(i.sector)}" rx="2" />
        ${showLabel ? `<text x="${(r.x + 4).toFixed(1)}" y="${(r.y + 14).toFixed(1)}" style="font-size:10px;fill:#fff;font-weight:600">${esc(shortName(i.name, Math.floor(r.w / 6)))}</text><text x="${(r.x + 4).toFixed(1)}" y="${(r.y + 26).toFixed(1)}" style="font-size:9px;fill:#ffffffcc">${num(i.branchNow)}</text>` : ''}
      </g>`;
    });
    treeEl.innerHTML = `<svg class="chart" viewBox="0 0 ${tw} ${th}" role="img" aria-label="Treemap of branches by institution">${cells}</svg>`;
    treeEl.querySelectorAll<SVGElement>('[data-abn]').forEach((g) => g.addEventListener('click', () => openInstitution((g as any).dataset.abn)));
    const sectorsShown = [...new Set(list.slice(0, 40).map((i) => i.sector))];
    (root.querySelector('#tree-legend') as HTMLElement).innerHTML = sectorsShown
      .map((s) => `<span class="legend-item"><span class="legend-swatch" style="background:${sectorColour(s)}"></span>${esc(national.sectorLabels[s] ?? s)}</span>`).join('');
  }

  root.querySelectorAll<HTMLButtonElement>('[data-rank]').forEach((b) =>
    b.addEventListener('click', () => { rankBy = b.dataset.rank as RankBy; root.querySelectorAll<HTMLButtonElement>('[data-rank]').forEach((x) => x.setAttribute('aria-pressed', String(x === b))); drawBars(); }),
  );
  drawBars();
}

function shortName(name: string, max = 26): string {
  let s = name
    .replace(/\s+(Limited|Ltd)\.?$/i, '')
    .replace(/Australia and New Zealand Banking Group/i, 'ANZ')
    .replace(/Commonwealth Bank of Australia/i, 'Commonwealth Bank')
    .replace(/National Australia Bank/i, 'NAB')
    .replace(/Westpac Banking Corporation/i, 'Westpac');
  if (s.length > max) s = s.slice(0, max - 1) + '…';
  return s;
}

// linear mix between two hex colours
function mix(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * Math.max(0, Math.min(1, t))));
  return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

// keep Institution import used for typing in shortName callers
export type { Institution };
